import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { RelocationForm } from "@/app/Relocations/Forms/RelocationForm";
import { RelocationStatus } from "@/gql/graphql";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { getRelocationUpdateInput } from "@/app/Relocations/Utils/getRelocationUpdateInput";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { relocationValidation } from "@/app/Relocations/Validation/relocationValidation";
import { useRelocation } from "@/app/Relocations/Hooks/useRelocation";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { RelocationFormValues } from "@/app/Relocations/Utils/getDefaultRelocationValues";
import { useOfferTripDefaultsUpdate } from "@/app/Relocations/Hooks/useOfferTripDefaultsUpdate";

export function RelocationEditDialog({
  id,
  onSuccess,
}: {
  id: string;
  onSuccess?: () => Promise<void> | void;
}) {
  const relocation = useRelocation(id);

  const { mutateAsync } = useGqlMutation(updateRelocationMutation);
  const offerTripDefaultsUpdate = useOfferTripDefaultsUpdate();
  const { close } = useDialogState();

  const linkedRelocations =
    relocation.group?.relocations.filter(
      (r) => r.id !== relocation.id && r.status !== RelocationStatus.Archived,
    ) ?? [];
  const hasLinkedRelocations = linkedRelocations.length > 0;

  return (
    <BaseDialog title="Edit relocation">
      {hasLinkedRelocations ? (
        <div className="mb-4 flex items-start gap-3 rounded-lg bg-blue-50 p-3">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">
              Changes will apply to all {linkedRelocations.length + 1} linked
              relocations
            </p>
            <p className="mt-1 text-blue-600">
              Delivery locations will remain unique for each relocation.
            </p>
          </div>
        </div>
      ) : null}
      <GenericForm<RelocationFormValues>
        validationSchema={toFormikValidationSchema(relocationValidation)}
        onSubmit={async (values) => {
          await mutateAsync({
            id: relocation.id,
            input: values,
          });

          await onSuccess?.();

          // Must resolve before this dialog unmounts, otherwise its useDialog
          // cleanup removes the trip defaults dialog it just opened.
          await offerTripDefaultsUpdate({
            officeAId:
              values.departureOffice?.connect ?? relocation.departureOffice.id,
            officeBId:
              values.deliveryOfficeIds?.[0] ?? relocation.deliveryOffice.id,
            values,
            awaitDialog: true,
          });

          await close();
        }}
        initialValues={getRelocationUpdateInput(relocation)}
      >
        <RelocationForm relocation={relocation} showAdditionalSettings />
        <DialogSubmitButton text={"Save"} />
      </GenericForm>
    </BaseDialog>
  );
}
